/**
 * Ad attribution for the office: the UTM tags and fbclid an ad's link carries
 * (Meta ads to /o/<handle> and /o/<handle>/tour), and the path the visit
 * landed on. Kept in sessionStorage as the visit's FIRST touch: a later
 * landing in the same tab does not replace it. Sent with the front door's
 * request-link and Google sign-in and with the public tour booking; the API
 * stores it on a NEW lead only. Values are trimmed to the API's bounds.
 */
export const ATTRIBUTION_KEY = 'tl_ad_attribution';

export interface AdAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  landingPath?: string;
}

const UTM_MAX = 150;
const FBCLID_MAX = 500;
const PATH_MAX = 300;

const PARAMS: [string, keyof AdAttribution][] = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_content', 'utmContent'],
  ['utm_term', 'utmTerm'],
];

/** Note the landing's tags, once per visit. Call on every page load (any route). */
export function captureAttribution(
  pathname: string = window.location.pathname,
  search: string = window.location.search,
): void {
  try {
    if (sessionStorage.getItem(ATTRIBUTION_KEY)) return;
    const q = new URLSearchParams(search);
    const a: AdAttribution = {};
    for (const [param, key] of PARAMS) {
      const v = q.get(param)?.trim();
      if (v) a[key] = v.slice(0, UTM_MAX);
    }
    const fbclid = q.get('fbclid')?.trim();
    if (fbclid && /^[A-Za-z0-9_.-]+$/.test(fbclid)) a.fbclid = fbclid.slice(0, FBCLID_MAX);
    if (Object.keys(a).length === 0) return;
    const path = pathname.startsWith('/') && !/[\s?#]/.test(pathname) ? pathname.slice(0, PATH_MAX) : null;
    if (path) a.landingPath = path;
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(a));
  } catch {
    // Storage blocked (private mode): the lead is made without its ad, nothing else changes.
  }
}

/** The visit's ad tags, or undefined when it came without any. */
export function readAttribution(): AdAttribution | undefined {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return undefined;
    const v = JSON.parse(raw) as unknown;
    if (!v || typeof v !== 'object') return undefined;
    const out: AdAttribution = {};
    for (const [, key] of PARAMS) {
      const s = (v as Record<string, unknown>)[key];
      if (typeof s === 'string' && s) out[key] = s.slice(0, UTM_MAX);
    }
    const f = (v as Record<string, unknown>).fbclid;
    if (typeof f === 'string' && /^[A-Za-z0-9_.-]+$/.test(f)) out.fbclid = f.slice(0, FBCLID_MAX);
    const p = (v as Record<string, unknown>).landingPath;
    if (typeof p === 'string' && p.startsWith('/')) out.landingPath = p.slice(0, PATH_MAX);
    return Object.keys(out).length ? out : undefined;
  } catch {
    return undefined;
  }
}
